import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import TimeEntryEditModal from './TimeEntryEditModal';
import AdminClockOutModal from './AdminClockOutModal';
import { useTranslation } from 'react-i18next';
import '../../styles/staff-scheduling.css';

const TimeManagementTab = ({ staffId, companyId, staff }) => {
  const { t } = useTranslation('staff');
  
  // State for active session
  const [activeSession, setActiveSession] = useState(null);
  const [activeSessionLoading, setActiveSessionLoading] = useState(true);
  const [departmentName, setDepartmentName] = useState('');
  
  // State for time entries
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(true);
  const [timeEntriesError, setTimeEntriesError] = useState(null);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('week');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;
  
  // Real-time timer
  const [currentTime, setCurrentTime] = useState(new Date());

  // Real-time timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load department name
  const loadDepartmentName = async (departmentId) => {
    if (!departmentId) return 'Unknown Department';
    
    try {
      const deptRef = doc(db, 'companies', companyId, 'departments', departmentId);
      const deptDoc = await getDoc(deptRef);
      
      if (deptDoc.exists()) {
        return deptDoc.data().name || 'Unknown Department';
      }
      return 'Unknown Department';
    } catch (error) {
      console.error('Error loading department:', error);
      return 'Unknown Department';
    }
  };

  // Load active session
  const loadActiveSession = async () => {
    if (!staffId || !companyId) {
      setActiveSessionLoading(false);
      return;
    }

    try {
      setActiveSessionLoading(true);
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      const sessionDoc = await getDoc(activeSessionRef);
      
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        const deptName = await loadDepartmentName(sessionData.departmentId);
        
        setActiveSession({
          staffId,
          departmentId: sessionData.departmentId,
          departmentName: deptName,
          clockIn: sessionData.clockIn?.toDate() || new Date(),
          onBreak: sessionData.onBreak || false,
          breakStart: sessionData.breakStart?.toDate(),
          deviceType: sessionData.deviceType || t('timeManagement.device.unknown'),
          location: sessionData.location || { lat: 0, lon: 0 }
        });
        setDepartmentName(deptName);
      } else {
        setActiveSession(null);
        setDepartmentName('');
      }
    } catch (error) {
      console.error('Error loading active session:', error);
      setActiveSession(null);
      setDepartmentName('');
    } finally {
      setActiveSessionLoading(false);
    }
  };

  // Load time entries
  const loadTimeEntries = async () => {
    if (!staffId || !companyId) {
      setTimeEntriesLoading(false);
      return;
    }

    try {
      setTimeEntriesLoading(true);
      setTimeEntriesError(null);
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setDate(startDate.getDate() - 30);
          break;
        default:
          startDate.setDate(startDate.getDate() - 7);
      }
      
      // Get time entries within date range
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      const startDateString = formatDateString(startDate);
      const endDateString = formatDateString(endDate);
      
      const timeEntriesQuery = query(
        timeEntriesRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(timeEntriesQuery);
      const entries = [];
      
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        
        // Load department name for each entry
        const deptName = await loadDepartmentName(data.departmentId);
        
        // Recalculate durations to handle both manual and automatic breaks
        const recalculatedDurations = recalculateTimeEntryDurations(data);
        
        entries.push({
          id: docSnapshot.id,
          ...data,
          departmentName: deptName,
          clockIns: data.clockIns || [],
          clockOuts: data.clockOuts || [],
          breakStarts: data.breakStarts || [],
          breakEnds: data.breakEnds || [],
          // Use recalculated durations instead of potentially incorrect Firebase values
          totalWorkDuration: Math.round(recalculatedDurations.workDuration / 60),
          totalBreakDuration: Math.round(recalculatedDurations.breakDuration / 60)
        });
      }
      
      setTimeEntries(entries);
    } catch (error) {
      console.error('Error loading time entries:', error);
      setTimeEntriesError(t('timeManagement.errors.loadTimeEntries'));
    } finally {
      setTimeEntriesLoading(false);
    }
  };

  // Helper function to format date as string
  const formatDateString = (date) => {
    return date.getFullYear() + '-' + 
           String(date.getMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getDate()).padStart(2, '0');
  };

  // Format duration in minutes to hours:minutes
  const formatDuration = (minutes) => {
    if (!minutes || minutes < 0) return '0:00';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${String(mins).padStart(2, '0')}`;
  };

  // Format time
  const formatTime = (date) => {
    if (!date) return '';
    try {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return '';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Handle admin clock out
  const handleAdminClockOut = () => {
    console.log('Opening admin clock out modal for:', staff?.name);
    setShowClockOutModal(true);
  };

  // Handle edit time entry
  const handleEditEntry = (entry) => {
    console.log('Opening edit modal for entry:', entry.id);
    setEditingEntry(entry);
    setShowEditModal(true);
  };

  // Handle add new time entry
  const handleAddEntry = () => {
    console.log('Opening add time entry modal for:', staff?.name);
    setEditingEntry(null); // Clear any existing entry data
    setShowAddModal(true);
  };

  // Handle time entry save from modal (both edit and add)
  const handleTimeEntrySave = async (updatedEntry) => {
    try {
      if (updatedEntry.isNewEntry) {
        // Add new entry to the list
        setTimeEntries(prevEntries => [updatedEntry, ...prevEntries]);
      } else {
        // Update existing entry in the list
        setTimeEntries(prevEntries => 
          prevEntries.map(entry => 
            entry.id === updatedEntry.id ? updatedEntry : entry
          )
        );
      }
      
      // Optionally refresh active session if it was affected
      if (activeSession) {
        await loadActiveSession();
      }
      
      console.log('Time entry saved successfully:', updatedEntry.id || 'new entry');
    } catch (error) {
      console.error('Error handling time entry save:', error);
    }
  };

  // Handle admin clock out success
  const handleAdminClockOutSuccess = async (result) => {
    try {
      console.log('Admin clock out successful:', result);
      
      // Refresh both active session and time entries to reflect changes
      await Promise.all([
        loadActiveSession(),
        loadTimeEntries()
      ]);
      
      // Close the modal
      setShowClockOutModal(false);
      
    } catch (error) {
      console.error('Error handling admin clock out success:', error);
    }
  };

  // Calculate session duration using current time
  const calculateSessionDuration = useCallback((session) => {
    if (!session || !session.clockIn) return 0;
    return Math.floor((currentTime - session.clockIn) / (1000 * 60)); // in minutes
  }, [currentTime]);

  // Calculate current break duration
  const getCurrentBreakDuration = useCallback((session) => {
    if (!session || !session.onBreak || !session.breakStart) return 0;
    return Math.floor((currentTime - session.breakStart) / (1000 * 60)); // in minutes
  }, [currentTime]);

  // Calculate work duration (total session minus break time)
  const calculateWorkDuration = useCallback((session) => {
    if (!session || !session.clockIn) return 0;
    
    const totalSessionDuration = calculateSessionDuration(session);
    const currentBreakDuration = getCurrentBreakDuration(session);
    
    return Math.max(0, totalSessionDuration - currentBreakDuration);
  }, [calculateSessionDuration, getCurrentBreakDuration]);

  // Get session status badge
  const getSessionStatusBadge = (entry) => {
    const status = entry.sessionStatus || 'Unknown';
    const statusClass = status.toLowerCase().replace(/\s+/g, '-');
    
    // Translate status text
    let translatedStatus = status;
    switch (status.toLowerCase()) {
      case 'clocked in':
        translatedStatus = t('timeManagement.timeEntries.status.clockedIn');
        break;
      case 'clocked out':
        translatedStatus = t('timeManagement.timeEntries.status.clockedOut');
        break;
      case 'on break':
        translatedStatus = t('timeManagement.timeEntries.status.onBreak');
        break;
      default:
        translatedStatus = status;
    }
    
    return (
      <span className={`status-badge ${statusClass}`}>
        {translatedStatus}
      </span>
    );
  };

  // Get multiple clock in/out times display
  const getClockTimesDisplay = (timestamps) => {
    if (!timestamps || timestamps.length === 0) return '-';
    
    if (timestamps.length === 1) {
      return formatTime(timestamps[0].toDate());
    }
    
    return (
      <div className="multiple-times">
        {timestamps.map((timestamp, index) => (
          <div key={index} className="time-entry">
            <span className="time-badge">{formatTime(timestamp.toDate())}</span>
          </div>
        ))}
        <div className="times-count">
          {timestamps.length} {t('timeManagement.timeEntries.multipleEntries')}
        </div>
      </div>
    );
  };

  // Get overtime warning
  const getOvertimeWarning = (minutes) => {
    const hours = minutes / 60;
    if (hours >= 10) {
      return { 
        level: 'critical', 
        message: `${formatDuration(minutes)} - ${t('timeManagement.currentSession.alerts.criticalOvertime')}` 
      };
    } else if (hours >= 8.5) {
      return { 
        level: 'warning', 
        message: `${formatDuration(minutes)} - ${t('timeManagement.currentSession.alerts.overtime')}` 
      };
    }
    return null;
  };

  // Modern Loading Component
  const ModernLoadingIndicator = ({ message }) => (
    <div className="modern-loading-container">
      <div className="modern-loading-content">
        <div className="loading-progress-bar">
          <div className="loading-progress-fill"></div>
        </div>
        <p className="loading-message">{message}</p>
      </div>
    </div>
  );

  // Load data on mount and when filters change
  useEffect(() => {
    if (staffId && companyId) {
      loadActiveSession();
      loadTimeEntries();
    }
  }, [staffId, companyId, dateFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(timeEntries.length / entriesPerPage);
  const paginatedEntries = timeEntries.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Get period summary title
  const getPeriodSummaryTitle = () => {
    let periodText;
    switch (dateFilter) {
      case 'today':
        periodText = t('timeManagement.periodSummary.today');
        break;
      case 'week':
        periodText = t('timeManagement.periodSummary.lastSevenDays');
        break;
      case 'month':
        periodText = t('timeManagement.periodSummary.lastThirtyDays');
        break;
      default:
        periodText = t('timeManagement.periodSummary.lastSevenDays');
    }
    return `${t('timeManagement.periodSummary.title')} (${periodText})`;
  };

  // Early return if required props are missing
  if (!staffId || !companyId) {
    return (
      <div className="time-management-tab">
        <div className="error-state">
          <p>{t('timeManagement.errors.missingInfo')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="time-management-tab">
      {/* Active Session Section */}
      <div className="time-section active-session-section">
        <h3>{t('timeManagement.currentSession.title')}</h3>
        
        {activeSessionLoading ? (
          <ModernLoadingIndicator message={t('timeManagement.loading.currentSession')} />
        ) : activeSession ? (
          <div className="active-session-card">
            <div className="session-header">
              <div className="session-info">
                <div className="session-status">
                  <span className={`status-badge ${activeSession.onBreak ? 'break' : 'working'}`}>
                    {activeSession.onBreak 
                      ? t('timeManagement.currentSession.status.onBreak') 
                      : t('timeManagement.currentSession.status.working')}
                  </span>
                  <span className="department-info">
                    {departmentName}
                  </span>
                </div>
                <div className="device-info">
                  <span className="device-text">{activeSession.deviceType}</span>
                </div>
              </div>
              <button 
                onClick={handleAdminClockOut}
                className="admin-action-btn clock-out"
                title="Clock out this staff member and adjust times if needed"
              >
                {t('timeManagement.currentSession.adminClockOut')}
              </button>
            </div>
            
            <div className="session-details">
              <div className="detail-row">
                <div className="detail-item">
                  <span className="label">{t('timeManagement.currentSession.labels.clockIn')}:</span>
                  <span className="value time-value">{formatTime(activeSession.clockIn)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t('timeManagement.currentSession.labels.totalDuration')}:</span>
                  <span className="value duration-highlight">
                    {formatDuration(calculateSessionDuration(activeSession))}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">{t('timeManagement.currentSession.labels.workTime')}:</span>
                  <span className="value work-duration">
                    {formatDuration(calculateWorkDuration(activeSession))}
                  </span>
                </div>
              </div>
              
              {activeSession.onBreak && (
                <div className="detail-row break-info">
                  <div className="detail-item">
                    <span className="label">{t('timeManagement.currentSession.labels.breakStarted')}:</span>
                    <span className="value time-value">{formatTime(activeSession.breakStart)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">{t('timeManagement.currentSession.labels.breakDuration')}:</span>
                    <span className="value break-duration">
                      {formatDuration(getCurrentBreakDuration(activeSession))}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="session-alerts">
              {(() => {
                const workMinutes = calculateWorkDuration(activeSession);
                const overtimeWarning = getOvertimeWarning(workMinutes);
                
                return (
                  <>
                    {overtimeWarning && (
                      <div className={`alert overtime ${overtimeWarning.level}`}>
                        ⚠️ {overtimeWarning.message}
                      </div>
                    )}
                    {activeSession.onBreak && getCurrentBreakDuration(activeSession) > 30 && (
                      <div className="alert long-break">
                        🕐 {t('timeManagement.currentSession.alerts.extendedBreak')} ({formatDuration(getCurrentBreakDuration(activeSession))})
                      </div>
                    )}
                    {calculateSessionDuration(activeSession) > 720 && (
                      <div className="alert long-session">
                        ℹ️ {t('timeManagement.currentSession.alerts.longSession')}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="no-active-session">
            <div className="empty-icon">
            
            </div>
            <h4>{t('timeManagement.currentSession.noActiveSession')}</h4>
            <p>{staff?.name} {t('timeManagement.currentSession.notClockedIn')}</p>
          </div>
        )}
      </div>

      {/* Time Entries Section */}
      <div className="time-section time-entries-section">
        <div className="section-header">
          <h3>{t('timeManagement.timeEntries.title')}</h3>
          <div className="section-controls">
            <button 
              onClick={handleAddEntry}
              className="add-entry-btn"
              title="Add new time entry manually"
            >
              + {t('timeManagement.timeEntries.addEntry')}
            </button>
            <select 
              value={dateFilter} 
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="date-filter"
            >
              <option value="today">{t('timeManagement.timeEntries.filters.today')}</option>
              <option value="week">{t('timeManagement.timeEntries.filters.week')}</option>
              <option value="month">{t('timeManagement.timeEntries.filters.month')}</option>
            </select>
            <button 
              onClick={() => {
                loadActiveSession();
                loadTimeEntries();
              }}
              className="refresh-btn"
              title="Refresh data"
              disabled={timeEntriesLoading || activeSessionLoading}
            >
              {t('timeManagement.refresh')}
            </button>
          </div>
        </div>

        {timeEntriesLoading ? (
          <ModernLoadingIndicator message={t('timeManagement.loading.timeEntries')} />
        ) : timeEntriesError ? (
          <div className="error-state">
            <div className="error-icon">❌</div>
            <p className="error-message">{timeEntriesError}</p>
            <button onClick={loadTimeEntries} className="retry-btn">
              🔄 {t('buttons.cancel')}
            </button>
          </div>
        ) : timeEntries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h4>{t('timeManagement.empty.noTimeEntries')}</h4>
            <p>{t('timeManagement.empty.noEntriesForPeriod')}</p>
            <button 
              onClick={handleAddEntry}
              className="add-entry-btn primary"
            >
              + {t('timeManagement.timeEntries.addFirstEntry')}
            </button>
          </div>
        ) : (
          <>
            <div className="time-entries-table">
              <div className="table-header">
                <div className="header-cell date-cell">{t('timeManagement.timeEntries.table.date')}</div>
                <div className="header-cell department-cell">{t('timeManagement.timeEntries.table.department')}</div>
                <div className="header-cell time-cell">{t('timeManagement.timeEntries.table.clockIn')}</div>
                <div className="header-cell time-cell">{t('timeManagement.timeEntries.table.clockOut')}</div>
                <div className="header-cell duration-cell">{t('timeManagement.timeEntries.table.workTime')}</div>
                <div className="header-cell duration-cell">{t('timeManagement.timeEntries.table.breakTime')}</div>
                <div className="header-cell status-cell">{t('timeManagement.timeEntries.table.status')}</div>
                <div className="header-cell actions-cell">{t('timeManagement.timeEntries.table.actions')}</div>
              </div>
              
              <div className="table-body">
                {paginatedEntries.map((entry) => (
                  <div key={entry.id} className="table-row">
                    <div className="cell date-cell">
                      <span className="date-display">{formatDate(entry.date)}</span>
                    </div>
                    <div className="cell department-cell">
                      <span className="department-name">{entry.departmentName}</span>
                    </div>
                    <div className="cell time-cell">
                      {getClockTimesDisplay(entry.clockIns)}
                    </div>
                    <div className="cell time-cell">
                      {getClockTimesDisplay(entry.clockOuts)}
                    </div>
                    <div className="cell duration-cell">
                      <span className="duration-display work-duration">
                        {formatDuration(entry.totalWorkDuration)}
                      </span>
                    </div>
                    <div className="cell duration-cell">
                      <span className="duration-display break-duration">
                        {formatDuration(entry.totalBreakDuration)}
                      </span>
                    </div>
                    <div className="cell status-cell">
                      {getSessionStatusBadge(entry)}
                    </div>
                    <div className="cell actions-cell">
                      <button 
                        onClick={() => handleEditEntry(entry)}
                        className="edit-btn"
                        title="Edit time entry"
                      >
                        {t('timeManagement.timeEntries.table.edit')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  {t('timeManagement.timeEntries.pagination.previous')}
                </button>
                
                <div className="page-info">
                  <span>{t('timeManagement.timeEntries.pagination.page')} {currentPage} {t('timeManagement.timeEntries.pagination.of')} {totalPages}</span>
                  <span className="entries-info">
                    ({timeEntries.length} {timeEntries.length === 1 
                      ? t('timeManagement.timeEntries.pagination.entry') 
                      : t('timeManagement.timeEntries.pagination.entries')})
                  </span>
                </div>
                
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  {t('timeManagement.timeEntries.pagination.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="time-section summary-section">
        <h3>{getPeriodSummaryTitle()}</h3>
        <div className="summary-stats">
          <div className="stat-item">
            <div className="stat-value">{timeEntries.length}</div>
            <div className="stat-label">{t('timeManagement.periodSummary.stats.totalEntries')}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              {formatDuration(
                timeEntries.reduce((total, entry) => total + (entry.totalWorkDuration || 0), 0)
              )}
            </div>
            <div className="stat-label">{t('timeManagement.periodSummary.stats.totalWorkHours')}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              {formatDuration(
                timeEntries.reduce((total, entry) => total + (entry.totalBreakDuration || 0), 0)
              )}
            </div>
            <div className="stat-label">{t('timeManagement.periodSummary.stats.totalBreakHours')}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              {timeEntries.filter(entry => entry.sessionStatus === 'Clocked Out').length}
            </div>
            <div className="stat-label">{t('timeManagement.periodSummary.stats.completedSessions')}</div>
          </div>
        </div>
      </div>

      {/* TimeEntry Edit Modal */}
      {showEditModal && (
        <TimeEntryEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEntry(null);
          }}
          timeEntry={editingEntry}
          companyId={companyId}
          staffId={staffId}
          staffName={staff?.name}
          onSave={handleTimeEntrySave}
        />
      )}

      {/* Add TimeEntry Modal */}
      {showAddModal && (
        <TimeEntryEditModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingEntry(null);
          }}
          timeEntry={null} // No existing entry - this will trigger "add mode"
          companyId={companyId}
          staffId={staffId}
          staffName={staff?.name}
          onSave={handleTimeEntrySave}
          isAddMode={true} // Explicitly indicate this is for adding a new entry
        />
      )}

      {/* Admin Clock Out Modal */}
      {showClockOutModal && (
        <AdminClockOutModal
          isOpen={showClockOutModal}
          onClose={() => setShowClockOutModal(false)}
          activeSession={activeSession}
          companyId={companyId}
          staffId={staffId}
          staffName={staff?.name}
          departmentName={departmentName}
          onSuccess={handleAdminClockOutSuccess}
        />
      )}
    </div>
  );
};

// Helper function to recalculate durations for display consistency
const recalculateTimeEntryDurations = (timeEntryData) => {
  try {
    // Convert Firestore timestamps to dates for calculation
    const clockIns = (timeEntryData.clockIns || []).map(ts => ts.toDate()).filter(d => d instanceof Date);
    const clockOuts = (timeEntryData.clockOuts || []).map(ts => ts.toDate()).filter(d => d instanceof Date);
    const breakStarts = (timeEntryData.breakStarts || []).map(ts => ts.toDate()).filter(d => d instanceof Date);
    const breakEnds = (timeEntryData.breakEnds || []).map(ts => ts.toDate()).filter(d => d instanceof Date);

    // Calculate total session time
    let totalSessionTime = 0;
    const maxSessions = Math.min(clockIns.length, clockOuts.length);
    
    for (let i = 0; i < maxSessions; i++) {
      if (clockOuts[i] > clockIns[i]) {
        totalSessionTime += (clockOuts[i] - clockIns[i]) / 1000; // in seconds
      }
    }

    // Calculate manual break duration
    let manualBreakDuration = 0;
    for (let j = 0; j < Math.min(breakStarts.length, breakEnds.length); j++) {
      if (breakEnds[j] > breakStarts[j]) {
        manualBreakDuration += (breakEnds[j] - breakStarts[j]) / 1000; // in seconds
      }
    }

    // Add automatic break adjustment if applicable
    let automaticBreakAdjustment = 0;
    if (timeEntryData.breakComplianceAdjusted && timeEntryData.breakComplianceAdjustment) {
      automaticBreakAdjustment = timeEntryData.breakComplianceAdjustment * 60; // convert minutes to seconds
    }

    // Total break time = manual breaks + automatic adjustment
    const totalBreakDuration = manualBreakDuration + automaticBreakAdjustment;

    // Work time = total session time - total break time
    const totalWorkDuration = Math.max(0, totalSessionTime - totalBreakDuration);

    return {
      workDuration: Math.round(totalWorkDuration),
      breakDuration: Math.round(totalBreakDuration),
      totalTime: Math.round(totalSessionTime)
    };
  } catch (error) {
    console.error('Error recalculating durations:', error);
    // Fall back to stored values
    return {
      workDuration: timeEntryData.totalWorkDuration || 0,
      breakDuration: timeEntryData.totalBreakDuration || 0,
      totalTime: (timeEntryData.totalWorkDuration || 0) + (timeEntryData.totalBreakDuration || 0)
    };
  }
};

export default TimeManagementTab;